import {
  createContext,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
  type ReactNode,
} from "react"

export interface EditScopeSnapshot {
  readonly isDirty: boolean
  readonly hasDraftChanges: boolean
  readonly hasTransientChanges: boolean
}

export interface EditScope {
  getSnapshot(): EditScopeSnapshot
  subscribe(listener: () => void): () => void
  /** Flush every currently dirty transient editor into its owning draft. */
  flushTransientEdits(): boolean
  /** Discard transient editor state. Durable application draft state is not reset. */
  discardTransientEdits(): void
}

export interface TransientEditParticipant {
  /** True only while this component owns changes not yet accepted into durable draft state. */
  readonly dirty: boolean
  /** Accept the transient value into draft state. Return false when local validation rejects it. */
  flush(): boolean
  /** Discard the transient value without updating draft state. */
  discard(): void
}

interface EditScopeRuntime extends EditScope {
  registerDirty(owner: object): void
  releaseDirty(owner: object): void
  releaseDirtyFromEffect(owner: object): void
  registerTransient(owner: object, participant: Omit<TransientEditParticipant, "dirty">): void
  releaseTransient(owner: object): void
  releaseTransientFromEffect(owner: object): void
}

const cleanSnapshot: EditScopeSnapshot = Object.freeze({
  isDirty: false,
  hasDraftChanges: false,
  hasTransientChanges: false,
})

const EditScopeContext = createContext<EditScopeRuntime | null>(null)

function createEditScopeRuntime(isActive: () => boolean): EditScopeRuntime {
  const listeners = new Set<() => void>()
  const dirtyOwners = new Set<object>()
  const transientParticipants = new Map<object, Omit<TransientEditParticipant, "dirty">>()
  let snapshot = cleanSnapshot

  const emitIfChanged = () => {
    const hasDraftChanges = dirtyOwners.size > 0
    const hasTransientChanges = transientParticipants.size > 0
    const isDirty = hasDraftChanges || hasTransientChanges
    if (
      snapshot.isDirty === isDirty &&
      snapshot.hasDraftChanges === hasDraftChanges &&
      snapshot.hasTransientChanges === hasTransientChanges
    )
      return
    snapshot = Object.freeze({ isDirty, hasDraftChanges, hasTransientChanges })
    for (const listener of listeners) listener()
  }

  const releaseDirty = (owner: object) => {
    if (!dirtyOwners.delete(owner)) return
    emitIfChanged()
  }

  const releaseTransient = (owner: object) => {
    if (!transientParticipants.delete(owner)) return
    emitIfChanged()
  }

  return {
    getSnapshot: () => snapshot,
    subscribe(listener) {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
    flushTransientEdits() {
      for (const [, participant] of [...transientParticipants]) {
        if (!participant.flush()) return false
      }
      return true
    },
    discardTransientEdits() {
      for (const [owner, participant] of [...transientParticipants]) {
        participant.discard()
        releaseTransient(owner)
      }
    },
    registerDirty(owner) {
      if (dirtyOwners.has(owner)) return
      dirtyOwners.add(owner)
      emitIfChanged()
    },
    releaseDirty,
    releaseDirtyFromEffect(owner) {
      if (!isActive()) return
      releaseDirty(owner)
    },
    registerTransient(owner, participant) {
      transientParticipants.set(owner, participant)
      emitIfChanged()
    },
    releaseTransient,
    releaseTransientFromEffect(owner) {
      if (!isActive()) return
      releaseTransient(owner)
    },
  }
}

export function EditScopeProvider({
  active = true,
  children,
}: {
  readonly active?: boolean
  readonly children: ReactNode
}) {
  const activeRef = useRef(active)
  activeRef.current = active
  const runtime = useMemo(() => createEditScopeRuntime(() => activeRef.current), [])
  return <EditScopeContext.Provider value={runtime}>{children}</EditScopeContext.Provider>
}

/** Returns the nearest edit scope, or null when the component is not hosted by one. */
export function useEditScope(): EditScope | null {
  return useContext(EditScopeContext)
}

/** Reactive dirty-state snapshot for Save/Discard UI and leave-guard composition. */
export function useEditScopeSnapshot(): EditScopeSnapshot {
  const scope = useContext(EditScopeContext)
  return useSyncExternalStore(
    scope?.subscribe ?? subscribeNothing,
    scope?.getSnapshot ?? getCleanSnapshot,
    scope?.getSnapshot ?? getCleanSnapshot
  )
}

/**
 * Registers durable application/document draft dirty state with the ambient scope.
 * Registration survives Activity-hidden effect cleanup and reconnects when visible.
 */
export function useEditScopeDirty(dirty: boolean) {
  const scope = useContext(EditScopeContext)
  const owner = useMemo(() => ({}), [])

  useLayoutEffect(() => {
    if (scope == null || !dirty) {
      scope?.releaseDirty(owner)
      return
    }
    scope.registerDirty(owner)
    return () => scope.releaseDirtyFromEffect(owner)
  }, [dirty, owner, scope])
}

/**
 * Registers a component-owned value that has not yet been accepted into the
 * durable draft. Dirty participants survive Activity-hidden effect cleanup.
 */
export function useTransientEditParticipant(participant: TransientEditParticipant) {
  const scope = useContext(EditScopeContext)
  const owner = useMemo(() => ({}), [])
  const participantRef = useRef(participant)
  participantRef.current = participant
  const stableParticipant = useMemo(
    () => ({
      flush: () => participantRef.current.flush(),
      discard: () => participantRef.current.discard(),
    }),
    []
  )

  useLayoutEffect(() => {
    if (scope == null || !participant.dirty) {
      scope?.releaseTransient(owner)
      return
    }
    scope.registerTransient(owner, stableParticipant)
    return () => scope.releaseTransientFromEffect(owner)
  }, [owner, participant.dirty, scope, stableParticipant])
}

function subscribeNothing() {
  return () => undefined
}

function getCleanSnapshot() {
  return cleanSnapshot
}
